import 'gleam-shared/embeds/IframelyMedia.scss'

import debounce from 'gleam-shared/helpers/debounce'
import onceDetaches from 'gleam-shared/browser/onceDetaches'
import safeParseJSON from 'gleam-shared/helpers/safeParse'
import onceChildAppends from 'gleam-shared/browser/onceChildAppends'

import { writeDom, cancelFastDom } from 'gleam-shared/browser/fastDom'

import DeadPreview from 'gleam-shared/embeds/DeadPreview'
import BasePreview from 'gleam-shared/embeds/BasePreview'

const HEIGHT_SIZER_CLASS = 'iframely-media__height-sizer'

const STABILIZER_CLASS = 'iframely-media__stabilizer'
const STABILIZER_SHOWN_CLASS = 'iframely-media__stabilizer--shown'

const PLACEHOLDER_CLASS = 'iframely-media__placeholder'
const PLACEHOLDER_HIDDEN_CLASS = 'iframely-media__placeholder--hidden'

const NEED_LONG_SHOW_DEBOUNCE_RE = /\b(fb\.watch|facebook\.com\/watch|instagram\.com|kickstarter\.com|kck\.st|twitter\.com|t\.co|x\.com|slideshare\.net)\b/i
const LONG_SHOW_DEBOUNCE = 1000
const SHORT_SHOW_DEBOUNCE = 300

const IFRAME_SELECTOR = 'iframe'
const SHOW_ON_LOAD_RE = /\b(kickstarter\.com|kck\.st|twitter\.com|t\.co|x\.com|slideshare\.net)\b/i
const WAIT_FOR_HEIGHT_RE = /\b(fb\.watch|facebook\.com|instagram\.com|tiktok\.com)\b/i

const INSTAGRAM_RE = /\binstagram\.com\b/i
const INSTAGRAM_UNWANTED_PADDING = 12

const TOO_SMALL_HEIGHT = 150
const TOO_LARGE_HEIGHT = 3000

export default function IframelyMedia(widthSizer, url, html) {
  try {
    if (!url || !html) {
      throw new Error('Expected arguments are missing!')
    }

    return RealPreview(widthSizer, url, html)
  } catch (err) {
    return DeadPreview('The iframely media', err)
  }
}

function RealPreview(widthSizer, url, html) {
  const preview = BasePreview({
    destroy,
    description: url
  })

  let iframeShown = false
  let iframeHeight = null

  const stabilizer = renderStabilizer(html)
  const heightSizer = findHeightSizer(widthSizer)
  const placeholder = findPlaceholder(widthSizer)
  const debouncedShow = debounce(lazyShow, chooseShowDebounce(url))
  const detachObserver = onceDetaches(widthSizer, destroy)
  const appendObserver = onceChildAppends(widthSizer, IFRAME_SELECTOR, onAppend)

  window.addEventListener('message', onMessage)
  writeDom(configure)

  return preview

  function configure() {
    heightSizer.append(stabilizer)
  }

  function destroy() {
    detachObserver.destroy()
    appendObserver.destroy()
    debouncedShow.cancel()
    cancelFastDom(show)
    cancelFastDom(configure)
    cancelFastDom(applyHeight)
    window.removeEventListener('message', onMessage)
    findIframe(widthSizer)?.removeEventListener('load', onLoad)
  }

  function onAppend() {
    if (SHOW_ON_LOAD_RE.test(url)) {
      findIframe(widthSizer).addEventListener('load', onLoad)
    }
  }

  function onLoad() {
    debouncedShow()
  }

  function onMessage({ source, data }) {
    if (source !== findIframe(widthSizer)?.contentWindow) {
      return
    }

    const payload = safeParseJSON(data)

    if (!payload) {
      return
    }

    const height = extractMediaHeight(payload)

    if (height) {
      resize(height)
    } else if (isMediaReady(payload)) {
      debouncedShow()
    } else if (isMediaPlaying(payload)) {
      debouncedShow()
      debouncedShow.flush()
    }
  }

  function resize(height) {
    if (height < TOO_SMALL_HEIGHT) {
      console.warn(`iframely: ${height} is too small and most likely a glitch`, url)
      return
    } else if (height > TOO_LARGE_HEIGHT) {
      console.warn(`iframely: ${height} is too large and most likely a glitch`, url)
      return
    }

    if (INSTAGRAM_RE.test(url)) {
      height -= INSTAGRAM_UNWANTED_PADDING
    }

    iframeHeight = height
    writeDom(applyHeight)
    debouncedShow()
  }

  function applyHeight() {
    if (iframeShown && iframeHeight) {
      // deliberately ovewriting whatever styles we had
      heightSizer.setAttribute('style', `height: ${iframeHeight}px`)
    }
  }

  function lazyShow() {
    writeDom(show)
  }

  function show() {
    if (iframeShown) {
      return
    }

    if (WAIT_FOR_HEIGHT_RE.test(url) && !iframeHeight) {
      return
    }

    iframeShown = true
    applyHeight()

    stabilizer.classList.add(STABILIZER_SHOWN_CLASS)
    placeholder.classList.add(PLACEHOLDER_HIDDEN_CLASS)

    preview.loadEmitter.emit()
  }
}

function extractMediaHeight(payload) {
  return payload.height ?? // iframely and tiktok embeds
    payload['twttr.embed']?.params?.[0]?.height // twitter embeds
}

function isMediaReady(payload) {
  return payload.method === 'widgetRendered' || // iframely embeds
    payload.type === 'ready' || // mixcloud and spotify embeds
    payload.eventName === 'UPDATE_STATE' // twitch
}

function isMediaPlaying(payload) {
  return payload.method === 'onPlay' // iframely embeds
}

function chooseShowDebounce(url) {
  if (NEED_LONG_SHOW_DEBOUNCE_RE.test(url)) {
    return LONG_SHOW_DEBOUNCE
  } else {
    return SHORT_SHOW_DEBOUNCE
  }
}

function findIframe(widthSizer) {
  return widthSizer.querySelector(IFRAME_SELECTOR)
}

function findHeightSizer(widthSizer) {
  return widthSizer.getElementsByClassName(HEIGHT_SIZER_CLASS)[0]
}

function findPlaceholder(widthSizer) {
  return widthSizer.getElementsByClassName(PLACEHOLDER_CLASS)[0]
}

function renderStabilizer(html) {
  const stabilizer = buildElement('section', STABILIZER_CLASS)
  stabilizer.innerHTML = html
  reviveScripts(stabilizer)

  return stabilizer
}

function reviveScripts(el) {
  const deadScripts = el.querySelectorAll('script')

  for (const deadScript of deadScripts) {
    const aliveScript = document.createElement('script')
    copyAttributes(deadScript, aliveScript)
    deadScript.parentNode.replaceChild(aliveScript, deadScript)
  }
}

function copyAttributes(fromEl, toEl) {
  for (const attr of fromEl.attributes) {
    toEl.setAttribute(attr.name, attr.value)
  }
}

function buildElement(tagName, className) {
  const element = document.createElement(tagName)
  element.className = className
  return element
}
